import React from 'react';
import { useTranslation } from 'react-i18next';
import { CareProfile, CareMedication } from '@/types';

interface EmergencyBriefProps {
  profile: CareProfile;
  medications: CareMedication[];
  onClose: () => void;
}

const EmergencyBrief: React.FC<EmergencyBriefProps> = ({
  profile,
  medications,
  onClose,
}) => {
  const { t } = useTranslation();
  const { dnrStatus, dnrSigningDate, dnrPhysicianName, firstName, lastName, dateOfBirth, insurancePrimary } = profile;

  const DNR_LABELS: Record<string, string> = {
    full: t('elder.dnr_full', 'Full DNR'),
    'dnr-cc': t('elder.dnr_comfort_care', 'DNR — Comfort Care Only'),
    partial: t('elder.dnr_partial', 'Partial DNR'),
    none: t('elder.dnr_none', 'No DNR on file'),
  };

  const activeMedications = medications.filter(med => !med.endDate || new Date(med.endDate) >= new Date());

  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 print:hidden">
      <div className="bg-white rounded-[2rem] p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-6 mb-6">
          <div className="h-20 w-20 flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-700">
            {initials || 'PD'}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {firstName} {lastName}
            </h1>
            <p className="text-gray-600 mt-1">DOB: {dateOfBirth}</p>
          </div>
        </div>

        {/* DNR Block */}
        {dnrStatus !== 'none' ? (
          <div className="bg-red-600 text-white p-6 rounded-2xl mb-6 print:bg-gray-200 print:text-black print:border print:border-black">
            <div className="text-lg font-bold mb-2">🚨 DO NOT RESUSCITATE — {DNR_LABELS[dnrStatus]}</div>
            <div className="text-sm opacity-90">
              {dnrSigningDate && (
                <p className="mb-1">{t('elder.signing_date', 'Signing Date:')} {dnrSigningDate}</p>
              )}
              {dnrPhysicianName && (
                <p>{t('elder.physician', 'Physician:')} {dnrPhysicianName}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-green-600 text-white p-4 rounded-2xl mb-6 print:bg-gray-200 print:text-black print:border print:border-black">
            ✓ {t('elder.no_dnr_on_file', 'No DNR on file')}
          </div>
        )}

        {/* Sections */}
        {/* 1. Primary Conditions */}
        <section className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t('elder.primary_conditions', 'Primary Conditions')}</h2>
          {profile.primaryConditions?.length ? (
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              {profile.primaryConditions.map((condition, index) => (
                <li key={index}>{condition}</li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 italic">{t('elder.none_reported', 'None reported')}</p>
          )}
        </section>

        {/* 2. Allergies */}
        <section className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t('elder.allergies', 'Allergies')}</h2>
          {profile.allergies?.length ? (
            <div className="flex flex-wrap gap-2">
              {profile.allergies.map((allergy, index) => (
                <span
                  key={index}
                  className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium border border-red-200"
                >
                  {allergy}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 italic">{t('elder.none_reported', 'None reported')}</p>
          )}
        </section>

        {/* 3. Active Medications */}
        <section className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t('elder.active_medications', 'Active Medications')}</h2>
          {activeMedications.length ? (
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              {activeMedications.map((med, index) => (
                <li key={index}>
                  <strong>{med.name}</strong> — {med.dosage}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 italic">{t('elder.none_active', 'None active')}</p>
          )}
        </section>

        {/* 4. Insurance */}
        <section className="mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t('elder.insurance_section', 'Insurance')}</h2>
          {insurancePrimary?.provider ? (
            <div className="text-gray-700">
              <p><strong>{t('elder.provider_label', 'Provider:')} </strong>{insurancePrimary.provider}</p>
              <p><strong>{t('elder.member_id_label', 'Member ID:')} </strong>{insurancePrimary.memberId}</p>
            </div>
          ) : (
            <p className="text-gray-600 italic">{t('elder.no_insurance_info', 'No insurance information available')}</p>
          )}
        </section>

        {/* Print Button */}
        <div className="mt-8 pt-4 border-t print:hidden">
          <button
            onClick={handlePrint}
            className="w-full bg-brand text-white py-3 px-4 rounded-xl font-semibold hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            {t('elder.print_share', 'Print / Share')}
          </button>
          <button
            onClick={onClose}
            className="mt-3 w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-xl font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            {t('common.close', 'Close')}
          </button>
        </div>

        {/* Print styles */}
        <style>{`
          @media print {
            @page {
              size: auto;
              margin: 1cm;
            }
            body {
              margin: 0;
            }
            .no-print {
              display: none !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default EmergencyBrief;